import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { AccessDenied } from '@/components/AccessDenied/AccessDenied';
import { useStores } from '@/hooks/useStores';
import useUserProfile from '@/hooks/useKeycloakUser';
import { $store, setStore } from '@/models/store';
import { $allowAnotherStore, $user } from '@/models/user';
import { GroupsEnum } from '@/types/users';
import { hasAnyGroup } from '@/utils/groups';

export const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const { keycloak, user, isLoading } = useUserProfile();

  if (isLoading) return <div>Загрузка...</div>;

  if (!keycloak.authenticated) {
    const ref = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?ref=${encodeURIComponent(ref)}`} replace />;
  }

  if (!user) return <div>Загрузка...</div>;
  return <>{children}</>;
};

export const RoleRoute = ({
  children,
  groups,
  mobileOnly = false,
}: {
  children: ReactNode;
  groups: GroupsEnum[];
  mobileOnly?: boolean;
}) => {
  const user = useUnit($user);
  const hasGroup = hasAnyGroup(user?.groups, groups);
  const mobileDevice =
    /Mobi|Android/i.test(navigator.userAgent) && !/DT40/i.test(navigator.userAgent);
  const allowed = hasGroup && (!mobileOnly || mobileDevice);

  if (!allowed) {
    return (
      <AccessDenied requiredGroups={groups} mobileOnlyDenied={Boolean(hasGroup && mobileOnly)} />
    );
  }
  return <>{children}</>;
};

export const StoreRoute = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const store = useUnit($store);
  const allowAnotherStore = useUnit($allowAnotherStore);
  const { stores, loading } = useStores();
  const isStoreSelection =
    location.pathname === '/section' || location.pathname.startsWith('/section/confirm/');

  useEffect(() => {
    if (!loading && !store && stores.length === 1) setStore(stores[0]);
  }, [loading, store, stores]);

  if (loading || (!store && stores.length === 1)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100 text-gray-500">
        Загрузка магазинов...
      </div>
    );
  }

  if (!store && stores.length > 1 && !isStoreSelection && !allowAnotherStore) {
    const nextPath = `${location.pathname}${location.search}`;
    return <Navigate to={`/section?next=${encodeURIComponent(nextPath)}`} replace />;
  }

  return <>{children}</>;
};
